import {
  APICourseRepository,
  APIProgressRepository,
  APISessionRepository,
  APIUserRepository,
} from './implementations/index.js';

/**
 * Repository Factory
 * Creates appropriate repository instances based on storage type
 * API-backed runtime storage only
 */
class RepositoryFactory {
  constructor(prismaClient = null) {
    this.storageType = this._normalizeStorageType(import.meta.env.VITE_STORAGE_TYPE);
    this.prismaClient = prismaClient;
    this._courseRepository = null;
    this._progressRepository = null;
    this._sessionRepository = null;
    this._userRepository = null;
  }

  _normalizeStorageType(storageType) {
    const normalized = String(storageType || 'api').trim().toLowerCase();
    if (normalized === 'api' || normalized === 'prisma' || normalized === 'localstorage') {
      return 'api';
    }
    return normalized;
  }

  /**
   * Initialize with Prisma client
   * Call this when Prisma becomes available
   */
  setPrismaClient(prismaClient) {
    if (!prismaClient) {
      throw new Error('Invalid PrismaClient provided');
    }
    this.prismaClient = prismaClient;
    this.storageType = 'api';
    // Reset cached repositories to pick up new Prisma client
    this.reset();
  }

  /**
   * Get Course Repository
   */
  getCourseRepository() {
    if (!this._courseRepository) {
      if (this.storageType !== 'api') {
        throw new Error(`Unknown storage type: ${this.storageType}`);
      }
      this._courseRepository = new APICourseRepository();
    }
    return this._courseRepository;
  }

  /**
   * Get Progress Repository
   */
  getProgressRepository() {
    if (!this._progressRepository) {
      if (this.storageType !== 'api') {
        throw new Error(`Unknown storage type: ${this.storageType}`);
      }
      this._progressRepository = new APIProgressRepository();
    }
    return this._progressRepository;
  }

  /**
   * Get Session Repository
   */
  getSessionRepository() {
    if (!this._sessionRepository) {
      if (this.storageType !== 'api') {
        throw new Error(`Unknown storage type: ${this.storageType}`);
      }
      this._sessionRepository = new APISessionRepository();
    }
    return this._sessionRepository;
  }

  /**
   * Get User Repository
   */
  getUserRepository() {
    if (!this._userRepository) {
      if (this.storageType !== 'api') {
        throw new Error(`Unknown storage type: ${this.storageType}`);
      }
      this._userRepository = new APIUserRepository();
    }
    return this._userRepository;
  }

  /**
   * Check if using Prisma storage
   */
  isPrisma() {
    return this.storageType === 'api';
  }

  /**
   * Reset all repositories (useful for testing)
   */
  reset() {
    this._courseRepository = null;
    this._progressRepository = null;
    this._sessionRepository = null;
    this._userRepository = null;
  }
}

// Export singleton instance
export const repositoryFactory = new RepositoryFactory();

// Export factory class for testing
export { RepositoryFactory };
